import React from "react";
import { Share2, Copy, Ban, Check, MessageSquare } from "lucide-react";
import { toast } from "sonner";
import { api, fmtTime } from "@/lib/api";
import { usePoll } from "@/hooks/usePoll";

const url = (t) => `${window.location.origin}/film/${t}`;
const STATUS = { pending: ["chip-REVIEW", "Awaiting couple"], approved: ["chip-KEEP", "Approved"], changes_requested: ["chip-REJECTED", "Changes requested"] };

const Review = ({ r, projectId, refresh }) => {
  const [cls, label] = STATUS[r.status] || STATUS.pending;
  const resolve = async (cid) => { await api.resolveFilmComment(projectId, r.id, cid); refresh(); };
  return (
    <div className={`studio-card p-4 space-y-2 ${r.active ? "" : "opacity-50"}`} data-testid={`film-review-${r.id}`}>
      <div className="flex items-center gap-2 flex-wrap">
        <span className="font-semibold text-sm flex-1">{r.title} <span className="metric-label">({r.kind})</span></span>
        <span className={`chip ${cls}`} data-testid={`film-review-status-${r.id}`}>{label}</span>
        {r.active && <button className="btn btn-ghost !py-1 text-xs" onClick={() => { navigator.clipboard?.writeText(url(r.token)); toast.success("Link copied"); }} data-testid={`film-review-copy-${r.id}`}><Copy size={12} />Copy link</button>}
        {r.active && <button className="btn btn-danger !py-1 text-xs" onClick={async () => { await api.revokeFilmReview(projectId, r.id); refresh(); }} data-testid={`film-review-revoke-${r.id}`}><Ban size={12} />Revoke</button>}
      </div>
      <div className="font-mono text-[11px] text-[#5fe9f5] break-all">{url(r.token)}</div>
      {r.decision?.note && <p className="text-xs text-[#CBD5E1]"><b>{r.decision.name}:</b> {r.decision.note}</p>}
      {r.comments.length > 0 && (
        <ul className="space-y-1" data-testid={`film-review-comments-${r.id}`}>
          {[...r.comments].sort((a, b) => a.t - b.t).map((c) => (
            <li key={c.id} className={`flex items-start gap-2 text-xs ${c.resolved ? "opacity-50 line-through" : ""}`}>
              <span className="font-mono text-[#E6B04E] w-12">{fmtTime(c.t)}</span>
              <span className="flex-1"><b>{c.name}</b>: {c.text}</span>
              {!c.resolved && <button className="text-[#34d399]" title="Mark done" onClick={() => resolve(c.id)} data-testid={`film-comment-resolve-${c.id}`}><Check size={13} /></button>}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};

export const FilmReviewPanel = ({ projectId, hasRender }) => {
  const { data, refresh } = usePoll(() => api.filmReviews(projectId), 10000, [projectId]);
  const create = async (kind) => {
    try { const r = await api.createFilmReview(projectId, { kind }); navigator.clipboard?.writeText(url(r.token)); toast.success("Private film link created & copied"); refresh(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };
  return (
    <div className="space-y-3" data-testid="film-review-panel">
      <div className="flex items-center gap-2 flex-wrap">
        <h2 className="font-head text-xl font-bold flex items-center gap-2 flex-1"><MessageSquare size={18} className="text-[#E6B04E]" />Couple Film Review</h2>
        <button className="btn btn-gold !py-1.5 text-sm" disabled={!hasRender} onClick={() => create("master")} data-testid="btn-share-master"><Share2 size={14} />Share master film</button>
        <button className="btn btn-ghost !py-1.5 text-sm" disabled={!hasRender} onClick={() => create("highlight")} data-testid="btn-share-highlight"><Share2 size={14} />Share highlight</button>
      </div>
      {!hasRender && <p className="text-xs text-[#64748B]">Render the film first, then share a private review link with the couple.</p>}
      {(data || []).map((r) => <Review key={r.id} r={r} projectId={projectId} refresh={refresh} />)}
    </div>
  );
};
